<template>
  <el-card>
    <!-- ── Header: the match identity, read-only ──────────────────────────────
         The time point is edited in the time point section, and the condition is
         shared with every other match that pairs it: neither is editable here. -->
    <el-row
      slot="header"
      type="flex"
      justify="space-between"
      align="middle"
    >
      <!-- Left: what this match is, plus its status -->
      <el-col class="match-card-identity">
        <el-tag size="mini">
          {{ timePoint.token }}
        </el-tag>
        <el-tag
          size="mini"
          type="info"
        >
          {{ conditionLabel }}
        </el-tag>
        <el-tag
          v-if="!readonly && hasNoAnalyses"
          size="mini"
          type="warning"
        >
          <i class="el-icon-warning-outline" />
          {{ $t('stability.noAnalyses') }}
        </el-tag>
      </el-col>

      <!-- Right: the actions, boxed together so they read as one block -->
      <el-col
        v-if="!readonly"
        class="match-card-actions"
      >
        <TableActionButton
          :label="$t('tableButtons.clone')"
          button-icon="fas fa-clone"
          :disabled="cardLoading"
          :click-function="requestClone"
        />
        <TableActionButton
          :label="$t('tableButtons.delete')"
          button-icon="fas fa-trash-alt"
          :loading="deleting"
          :disabled="cardLoading && !deleting"
          :click-function="confirmDelete"
        />
      </el-col>
    </el-row>

    <!-- ── Body ─────────────────────────────────────────────────────────────── -->
    <div v-loading="cardLoading">
      <!-- Due date: never written from here, the API computes it from the MAX
           lead time of the analyses of this match -->
      <el-row
        type="flex"
        align="middle"
        :gutter="8"
      >
        <el-col :span="12">
          {{ $t('stability.matches.toDate') }}
        </el-col>
        <el-col :span="12">
          {{ formattedToDate }}
        </el-col>
      </el-row>

      <el-divider />

      <!-- Analysis select: remote-searchable multi-select with built-in tags -->
      <el-row
        v-if="!readonly || !hasNoAnalyses"
        :gutter="8"
      >
        <el-col
          v-if="!readonly"
          :span="6"
        >
          <el-select
            v-model="searchMode"
            size="mini"
          >
            <el-option
              value="code"
              :label="$t('stability.searchByCode')"
            />
            <el-option
              value="description"
              :label="$t('stability.searchByDescription')"
            />
          </el-select>
        </el-col>
        <el-col :span="readonly ? 24 : 18">
          <el-select
            ref="analysisSelect"
            v-model="localSelectedIds"
            multiple
            filterable
            remote
            :remote-method="remoteSearch"
            :loading="searchLoading"
            :disabled="readonly"
            :placeholder="
              readonly
                ? $t('stability.noAnalyses')
                : $t('stability.searchAnalyses')
            "
            size="mini"
            class="full-width"
            @change="onSelectChange"
          >
            <el-option
              v-for="opt in analysisOptions"
              :key="opt.id"
              :value="opt.id"
              :label="`${opt.code} - ${opt.description}`"
              :disabled="selectedAnalysisIds.has(opt.id)"
            />
          </el-select>
        </el-col>
      </el-row>

      <span v-if="readonly && hasNoAnalyses">
        {{ $t('stability.noAnalyses') }}
      </span>

      <!-- Due date of every single analysis (SMP-1625): the one above is only
           the MAX. The children of a fixed class are listed under their parent -->
      <template v-if="analysisDueRows.length">
        <el-divider />
        <div class="analysis-due-dates">
          <el-row
            v-for="row in analysisDueRows"
            :key="row.key"
            type="flex"
            :gutter="8"
          >
            <el-col
              :span="18"
              :class="{ 'analysis-due-child': row.isChild }"
            >
              {{ row.label }}
            </el-col>
            <el-col
              :span="6"
              class="analysis-due-date"
            >
              {{ row.toDate }}
            </el-col>
          </el-row>
        </div>
      </template>

      <template v-if="labSampleCodes.length">
        <el-divider />
        <el-row
          type="flex"
          align="middle"
          :gutter="8"
        >
          <el-col :span="24">
            <el-tag
              v-for="code in labSampleCodes"
              :key="code"
              size="mini"
              type="info"
            >
              {{ code }}
            </el-tag>
          </el-col>
        </el-row>
      </template>
    </div>
  </el-card>
</template>

<script>
  import moment from 'moment';
  import { stabilityApi } from '@/api';
  import TableActionButton from '@/components/TableActionButton.vue';

  export default {
    name: 'MatchCard',
    components: { TableActionButton },

    props: {
      // The match this card stands for, with its condition, analyses and entries
      match: { type: Object, required: true },
      // The time point the match pairs; owned and edited by the time point section
      timePoint: { type: Object, required: true },
      configId: { type: Number, required: true },
      readonly: { type: Boolean, default: false },
    },

    data() {
      return {
        // Per-card loading for destructive ops (delete, add/remove analysis)
        cardLoading: false,
        // Narrower than cardLoading: only the delete, so the trash icon does
        // not spin while an analysis is being added
        deleting: false,

        // Analysis select
        searchMode: 'code',
        searchResults: [],
        searchLoading: false,
        localSelectedIds: (this.match.stabilityConfigurationAnalyses || []).map(
          (sca) => sca.analysisId
        ),
      };
    },

    computed: {
      condition() {
        return this.match.stabilityCondition || null;
      },
      conditionLabel() {
        if (!this.condition) return '-';
        const { temperature, humidity, matrix } = this.condition;
        const humidityPart = humidity != null ? ` ${humidity}%RH` : '';
        const matrixPart = matrix ? ` / ${matrix}` : '';
        return `${temperature}°C${humidityPart}${matrixPart}`;
      },
      formattedToDate() {
        // Local, not UTC: `todate` is a `timestamp without time zone`
        if (!this.match.toDate) return '-';
        return moment(this.match.toDate).format('DD/MM/YYYY');
      },
      // The API already orders them: each parent followed by its children
      analysisDueRows() {
        return (this.match.analysisDueDates || []).map((due) => ({
          // Not the analysis alone: a single that is also the child of a fixed
          // class, or a child shared by two classes, appears twice
          key: `${due.parentAnalysisId ?? ''}-${due.analysisId}`,
          label: `${due.analysisCode} - ${due.analysisDescription}`,
          isChild: due.parentAnalysisId != null,
          // A plain calendar day (`YYYY-MM-DD`): no time zone to undo here
          toDate: due.toDate
            ? moment(due.toDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
            : '-',
        }));
      },
      labSampleCodes() {
        return (this.match.stabilityEntries || [])
          .filter((e) => e.labSample)
          .map((e) => e.labSample.labSampleCode);
      },
      hasNoAnalyses() {
        return (
          !this.match.stabilityConfigurationAnalyses ||
          this.match.stabilityConfigurationAnalyses.length === 0
        );
      },
      selectedAnalysisIds() {
        if (!this.match.stabilityConfigurationAnalyses) return new Set();
        return new Set(
          this.match.stabilityConfigurationAnalyses.map((sca) => sca.analysisId)
        );
      },
      // Options visible in the dropdown = selected analyses + remote search results (deduped)
      analysisOptions() {
        const selected = (this.match.stabilityConfigurationAnalyses || [])
          .filter((sca) => sca.analysis)
          .map((sca) => sca.analysis);
        const selectedIds = new Set(selected.map((a) => a.id));
        const extra = this.searchResults.filter((a) => !selectedIds.has(a.id));
        return [...selected, ...extra];
      },
    },

    watch: {
      // eslint-disable-next-line func-names
      'match.stabilityConfigurationAnalyses': function (analyses) {
        this.localSelectedIds = (analyses || []).map((sca) => sca.analysisId);
        this.searchResults = [];
      },
    },

    methods: {
      // ── Delete match ─────────────────────────────────────────────────────

      async confirmDelete() {
        try {
          await this.$confirm(
            this.$t('stability.matches.deleteConfirm'),
            this.$t('global.warning'),
            {
              confirmButtonText: this.$t('stability.matches.delete'),
              cancelButtonText: this.$t('global.cancel'),
            }
          );
        } catch {
          return;
        }
        await this.deleteMatch();
      },

      async deleteMatch() {
        this.cardLoading = true;
        this.deleting = true;
        try {
          // The condition survives: other matches may still pair it
          await stabilityApi.deleteMatch(this.configId, this.match.id);
          this.$emit('refresh');
        } catch (err) {
          this.notifyError(err);
        } finally {
          this.cardLoading = false;
          this.deleting = false;
        }
      },

      // ── Clone ────────────────────────────────────────────────────────────

      requestClone() {
        this.$emit('clone-requested', {
          matchId: this.match.id,
          sourceTimePointId: this.timePoint.id,
          sourceTemperature: this.condition ? this.condition.temperature : null,
          sourceHumidity: this.condition
            ? this.condition.humidity ?? null
            : null,
          sourceMatrix: this.condition ? this.condition.matrix || null : null,
        });
      },

      // ── Analysis remote search ───────────────────────────────────────────

      remoteSearch(query) {
        if (!query) {
          this.searchResults = [];
          return;
        }
        this.searchLoading = true;
        const key = this.searchMode === 'code' ? 'code' : 'description';
        stabilityApi
          .searchAnalyses(key, query)
          .then((res) => {
            this.searchResults = res.data.filter(
              (a) => !this.selectedAnalysisIds.has(a.id)
            );
          })
          .catch(() => {
            this.searchResults = [];
          })
          .finally(() => {
            this.searchLoading = false;
          });
      },

      // Element emits `change` for both directions, but `remove-tag` only for
      // the tag's close button: clicking an already-selected option in the
      // dropdown deselects it silently. Diffing here covers both paths exactly
      // once, so a deselect can no longer drop the tag without a DELETE.
      onSelectChange(newIds) {
        const added = newIds.find((id) => !this.selectedAnalysisIds.has(id));
        if (added !== undefined) {
          this.addAnalysis(added);
          return;
        }

        const removed = [...this.selectedAnalysisIds].find(
          (id) => !newIds.includes(id)
        );
        if (removed === undefined) return;
        const sca = (this.match.stabilityConfigurationAnalyses || []).find(
          (s) => s.analysisId === removed
        );
        if (sca) this.removeAnalysis(sca.id);
      },

      // The select has already changed locally; on a rejection the tags must go
      // back to what the API actually holds, or a phantom tag survives
      resyncSelected() {
        this.localSelectedIds = (
          this.match.stabilityConfigurationAnalyses || []
        ).map((sca) => sca.analysisId);
      },

      // ── Analysis add / remove, on the match ──────────────────────────────
      // The refresh is what brings the recomputed toDate to screen, here and in
      // the due-date column of the time point section, without a page reload.

      // Element leaves the dropdown open after a multiple selection, and the
      // refresh then swaps its content from the search results to the already
      // associated analyses. Closing it removes that trap — and a stray second
      // click on the row just added; Element clears the query on close.
      closeAnalysisDropdown() {
        if (this.$refs.analysisSelect) this.$refs.analysisSelect.blur();
      },

      async addAnalysis(analysisId) {
        this.cardLoading = true;
        try {
          await stabilityApi.addAnalysis(this.configId, this.match.id, {
            analysisId,
          });
          this.closeAnalysisDropdown();
          this.$emit('refresh');
        } catch (err) {
          this.resyncSelected();
          this.notifyError(err);
        } finally {
          this.cardLoading = false;
        }
      },

      async removeAnalysis(scaId) {
        this.cardLoading = true;
        try {
          await stabilityApi.removeAnalysis(
            this.configId,
            this.match.id,
            scaId
          );
          this.$emit('refresh');
        } catch (err) {
          this.resyncSelected();
          this.notifyError(err);
        } finally {
          this.cardLoading = false;
        }
      },

      notifyError(err) {
        this.$notify({
          type: 'error',
          showClose: true,
          message:
            (err.response &&
              err.response.data &&
              err.response.data.error &&
              err.response.data.error.message) ||
            this.$t('stability.errors.generic'),
        });
      },
    },
  };
</script>

<style lang="scss">
  .el-card {
    background-color: $--color-black-lighter;
    border-color: $--color-black;

    .el-card__header {
      border-bottom-color: $--color-black;
    }

    // Space consecutive tags (condition header, lab-sample codes)
    .el-tag + .el-tag {
      margin-left: 0.25rem;
    }

    // el-col defaults `span` to 24, so a col with no span renders .el-col-24
    // and is width: 100%. Both halves would claim the full row, so the width
    // is handed back to flex here: the identity grows and shrinks...
    .match-card-identity {
      flex: 1 1 auto;
      min-width: 0;
      width: auto;
    }

    // ...and the action block hugs its two icons, never stretching. The app has
    // no convention for a group of icon actions — el-button-group draws its box
    // from the buttons' own borders, which these do not have — so the box comes
    // from the theme variables.
    .match-card-actions {
      flex: 0 0 auto;
      width: auto;
      border: $--border-base;
      border-radius: $--border-radius-base;
      padding: 0.15rem 0.25rem;
      white-space: nowrap;
    }

    // Per-analysis due dates: the children of a fixed class sit indented under
    // their parent, the dates line up on the right
    .analysis-due-dates {
      font-size: $--font-size-small;
      line-height: 1.6;
    }

    .analysis-due-child {
      padding-left: 1rem !important;
    }

    .analysis-due-date {
      text-align: right;
      white-space: nowrap;
    }

    // Override el-select's hardcoded type="info" tags to match filter-tag style (primary)
    .el-select__tags .el-tag--info {
      background-color: mix($--tag-primary-color, $--color-white, 10%);
      border-color: mix($--tag-primary-color, $--color-white, 20%);
      color: $--tag-primary-color;

      .el-tag__close {
        background-color: transparent;
        color: $--tag-primary-color;

        &:hover {
          color: $--color-white;
          background-color: $--tag-primary-color;
        }
      }
    }
  }
</style>
